import { default as axiosGlobal } from 'axios';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { PUBLIC_REPORTS, PublicReportsResponse } from 'src/api';
import axios from 'src/axios';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  handleErrorToast,
  useFileUploadContext,
} from 'src/components/molecules';
import { useUploadReportDocumentation } from 'src/components/molecules/FileUpload/hooks/useUploadReportDocumentation';
import { UploadSource, useGetPublicFeaturesQuery } from 'src/graphql';
import {
  trackOprReportSent,
  trackOprReportSubmitted,
} from 'src/utils/analytics/track/opr';
import * as OpenReportingCreators from '../../../creators/OpenReporting.creators';
import * as Types from '../../../types';
import Password from './Password';

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const SubmitReport: FC<Props> = ({
  fetchPublicSession,
  publicReport,
  onSubmit,
  setReportJustSubmitted,
  setIsSubmitting,
  setUploadErrors,
  organisation,
  solutionToken,
  loginValues,
  setSolutionToken,
  setFetchedChallenge,
  setUserAnswer,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { files } = useFileUploadContext();

  const { uploadFile } = useUploadReportDocumentation({
    uploadSource: UploadSource.Opr,
  });

  const { data: publicFeatures } = useGetPublicFeaturesQuery({
    slug: organisation,
  });

  const segmentEnabled = isRequiredFeatureEnabled(
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [],
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const { organisation: org } = useSelector(
    (state: Types.State) => state.organisation,
  );

  const handleFailedReportSubmission = async (e: unknown) => {
    let code: Types.TranslationKey = 'Errors.createReportFailed';
    if (axiosGlobal.isAxiosError(e)) {
      const errorCode = e.response?.data?.errors?.[0]?.code;
      if (errorCode) {
        code = `public.CreatePublicReport.${errorCode}` as Types.TranslationKey;
      }
    }

    setIsSubmitting(false);
    handleErrorToast({
      toastMessage: t(code),
    });
    setSolutionToken('');
    setFetchedChallenge({ reference: '', challenge: '' });
    setUserAnswer('');
  };

  const submit = async () => {
    setIsSubmitting(true);

    const uniqueFileTypes = [
      ...new Set(
        files
          .map((file) => file.type)
          .filter((value) => typeof value !== 'undefined'),
      ),
    ] as string[];

    if (segmentEnabled) {
      const trackOprReportSentProperties = {
        organisation_id: org?.id,
        report_id: publicReport.id,
        total_file_size: files.reduce((acc, file) => acc + file.size, 0),
        total_file_count: files.length,
        unique_file_types: uniqueFileTypes,
        category: publicReport.category,
        relationship_to_company: publicReport.relationshipToCompany,
        is_anonymous: !publicReport?.email,
      };

      trackOprReportSent(trackOprReportSentProperties);
    }

    if (publicReport.category === '') {
      setIsSubmitting(false);

      handleErrorToast({
        toastMessage: 'Report category must be selected to submit a report.',
      });
      return;
    }

    let reporter: Types.PublicReportingAccount | null = null;
    let submitResponse: PublicReportsResponse | null = null;

    try {
      const response = await axios.post<PublicReportsResponse>(
        PUBLIC_REPORTS,
        {
          publicReport,
        },
        {
          headers: {
            authorization: solutionToken ?? '',
          },
        },
      );
      submitResponse = response.data;
      reporter = submitResponse?.data?.account;

      dispatch(setReportJustSubmitted(true));
    } catch (e) {
      await handleFailedReportSubmission(e);
      return;
    }

    let errorsCount: number = 0;
    for (const file of files) {
      try {
        await uploadFile({
          reportId: publicReport.id,
          eventId: publicReport.evidence.events[0].id,
          file,
        });
      } catch (error) {
        errorsCount++;
      }
    }

    setUploadErrors(errorsCount);

    try {
      if (reporter === null) {
        throw new Error('error submitting OPR report');
      }

      if (segmentEnabled) {
        const trackOprReportSubmittedProperties = {
          organisation_id: submitResponse?.data?.report?.Organisation?.id,
          report_id: publicReport.id,
          total_file_size: files.reduce((acc, file) => acc + file.size, 0),
          total_file_count: files.length,
          successful_file_count: files.length - errorsCount,
          failed_file_count: errorsCount,
          file_types: uniqueFileTypes,
          category: publicReport.category,
          relationship_to_company: publicReport.relationshipToCompany,
          is_anonymous: !publicReport?.email,
        };
        trackOprReportSubmitted(trackOprReportSubmittedProperties);
      }

      onSubmit({
        redirect: `/${organisation}/open-reporting/success`,
        loginValues: {
          ...loginValues,
          username: reporter?.username,
        },
        publicReporterAccount: null,
      });
      setIsSubmitting(false);

      await fetchPublicSession();
    } catch (e) {
      await handleFailedReportSubmission(e);
      return;
    }
  };

  return <Password submit={submit} changePassword={false} isLocalised={true} />;
};

const mapStateToProps = (state: Types.State) => {
  const { logo: _logo, ...publicReport } = state.openReporting
    .openReportReducer as Types.PublicReport;
  const openReporting = {
    changePasswordValues: state.openReporting.changePasswordReducer,
    publicReport,
    loginValues: state.openReporting.loginReducer,
    ...state.openReporting?.openReportingReducer,
  };

  return {
    organisation: openReporting.publicReport?.organisation,
    publicReport: openReporting.publicReport,
    solutionToken: openReporting.solutionToken,
    loginValues: openReporting.loginValues,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      ...OpenReportingCreators,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(SubmitReport);
